import { registerLocaleData } from '@angular/common';

/**
 * Cache of already registered locale codes to avoid redundant loading.
 */
const loadedLocales = new Set<string>();

/**
 * Lazy loaders for supported Angular CLDR locales.
 * Using explicit dynamic imports allows bundlers (Webpack) to create
 * optimized lazy chunks on demand without wildcard path warnings.
 */
const LOCALE_IMPORTERS: Record<string, () => Promise<{ default: any }>> = {
	it: () => import('@angular/common/locales/it'),
	en: () => import('@angular/common/locales/en'),
	es: () => import('@angular/common/locales/es'),
	fr: () => import('@angular/common/locales/fr'),
	de: () => import('@angular/common/locales/de'),
	pt: () => import('@angular/common/locales/pt'),
};

/**
 * Dynamically loads and registers the Angular CLDR locale data for any given language code.
 * E.g., 'it' -> '@angular/common/locales/it'
 */
export async function loadAngularLocale(langCode?: string): Promise<void> {
	if (!langCode) return;
	const code = langCode.toLowerCase().split('-')[0];

	if (loadedLocales.has(code)) {
		return;
	}

	const importer = LOCALE_IMPORTERS[code];
	if (!importer) {
		return;
	}

	try {
		const localeModule = await importer();
		if (localeModule && localeModule.default) {
			registerLocaleData(localeModule.default, code);
			registerLocaleData(localeModule.default, `${code}-${code.toUpperCase()}`);
			loadedLocales.add(code);
		}
	} catch (error) {
		console.warn(`[loadAngularLocale] Could not load locale for "${code}":`, error);
	}
}
